'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { SlidersHorizontal, Star, MapPin, Clock, Users, Heart, ArrowUpDown, List, ChevronLeft, ChevronRight, Zap, Share2, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

const PACKAGES = [
{
  id: 'pkg-001',
  name: 'Maasai Mara Great Migration',
  operator: 'Savanna Adventures',
  region: 'Maasai Mara',
  country: 'Kenya',
  duration: 4,
  durationType: 'days',
  category: 'Big Five',
  badge: 'Best Seller',
  badgeColor: 'bg-safari-yellow text-gray-900',
  price: 128000,
  deposit: 38400,
  currency: 'KES',
  rating: 4.9,
  reviewCount: 312,
  groupMin: 2,
  groupMax: 8,
  spotsLeft: 3,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16d806e5f-1772249664669.png",
  imageAlt: 'Wildebeest migration across Maasai Mara river at golden hour',
  tags: ['Big Five', 'Migration', 'Photography'],
  includes: ['4 Game Drives', 'Full Board', 'Park Fees'],
  saved: false
},
{
  id: 'pkg-002',
  name: 'Amboseli Elephant Kingdom',
  operator: 'Wild Horizons Kenya',
  region: 'Amboseli',
  country: 'Kenya',
  duration: 3,
  durationType: 'days',
  category: 'Family',
  badge: 'Family Pick',
  badgeColor: 'bg-green-100 text-green-800',
  price: 89500,
  deposit: 26850,
  currency: 'KES',
  rating: 4.7,
  reviewCount: 187,
  groupMin: 2,
  groupMax: 12,
  spotsLeft: 7,
  image: "https://images.unsplash.com/photo-1727252734589-147155b958be",
  imageAlt: 'Elephant herd walking in Amboseli with Mount Kilimanjaro in background',
  tags: ['Elephants', 'Family', 'Kilimanjaro Views'],
  includes: ['3 Game Drives', 'Half Board', 'Park Fees'],
  saved: true
},
{
  id: 'pkg-003',
  name: 'Serengeti Luxury Tented Camp',
  operator: 'Tanzania Exclusive Safaris',
  region: 'Serengeti',
  country: 'Tanzania',
  duration: 5,
  durationType: 'days',
  category: 'Luxury',
  badge: 'Premium',
  badgeColor: 'bg-amber-100 text-amber-800',
  price: 285000,
  deposit: 85500,
  currency: 'KES',
  rating: 5.0,
  reviewCount: 94,
  groupMin: 2,
  groupMax: 6,
  spotsLeft: 2,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fad7d61b-1772249789689.png",
  imageAlt: 'Luxury safari tent at sunset with giraffes in the background on Serengeti plains',
  tags: ['Luxury', 'Big Five', 'Sundowners'],
  includes: ['6 Game Drives', 'All Inclusive', 'Bush Dinner'],
  saved: false
},
{
  id: 'pkg-004',
  name: 'Tsavo East & West Explorer',
  operator: 'Red Elephant Safaris',
  region: 'Tsavo',
  country: 'Kenya',
  duration: 3,
  durationType: 'days',
  category: 'Adventure',
  badge: null,
  badgeColor: '',
  price: 67000,
  deposit: 20100,
  currency: 'KES',
  rating: 4.5,
  reviewCount: 142,
  groupMin: 2,
  groupMax: 10,
  spotsLeft: 9,
  image: "https://images.unsplash.com/photo-1605302596046-1649bec79747",
  imageAlt: 'Red elephants walking in Tsavo National Park dry savannah landscape',
  tags: ['Red Elephants', 'Adventure', 'Value'],
  includes: ['4 Game Drives', 'Breakfast', 'Park Fees'],
  saved: false
},
{
  id: 'pkg-005',
  name: 'Ngorongoro Crater Rim Safari',
  operator: 'Crater View Expeditions',
  region: 'Ngorongoro',
  country: 'Tanzania',
  duration: 2,
  durationType: 'days',
  category: 'Day Trip',
  badge: 'New',
  badgeColor: 'bg-blue-100 text-blue-800',
  price: 54000,
  deposit: 16200,
  currency: 'KES',
  rating: 4.8,
  reviewCount: 67,
  groupMin: 1,
  groupMax: 6,
  spotsLeft: 5,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b039d2d6-1772252846157.png",
  imageAlt: 'Aerial view of Ngorongoro Crater with wildlife grazing on crater floor',
  tags: ['Crater', 'Big Five', 'Short Break'],
  includes: ['Crater Descent', 'Picnic Lunch', 'Guide'],
  saved: false
},
{
  id: 'pkg-006',
  name: 'Lake Nakuru Flamingo & Rhino',
  operator: 'Rift Valley Safaris',
  region: 'Lake Nakuru',
  country: 'Kenya',
  duration: 2,
  durationType: 'days',
  category: 'Birding',
  badge: 'Eco Pick',
  badgeColor: 'bg-green-100 text-green-800',
  price: 42000,
  deposit: 12600,
  currency: 'KES',
  rating: 4.6,
  reviewCount: 203,
  groupMin: 2,
  groupMax: 8,
  spotsLeft: 12,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf5d4063-1772249665223.png",
  imageAlt: 'Thousands of pink flamingos on Lake Nakuru with rhino in foreground',
  tags: ['Flamingos', 'Rhino', 'Birding'],
  includes: ['2 Game Drives', 'Breakfast', 'Park Fees'],
  saved: true
},
{
  id: 'pkg-007',
  name: 'Samburu Unique Wildlife Safari',
  operator: 'Northern Frontier Safaris',
  region: 'Samburu',
  country: 'Kenya',
  duration: 4,
  durationType: 'days',
  category: 'Unique Wildlife',
  badge: null,
  badgeColor: '',
  price: 115000,
  deposit: 34500,
  currency: 'KES',
  rating: 4.7,
  reviewCount: 88,
  groupMin: 2,
  groupMax: 8,
  spotsLeft: 6,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e738ff30-1775894306281.png",
  imageAlt: 'Reticulated giraffe and Grevy zebra in Samburu National Reserve dry landscape',
  tags: ['Rare Species', 'Off-the-beaten', 'Authentic'],
  includes: ['5 Game Drives', 'Full Board', 'Night Drive'],
  saved: false
},
{
  id: 'pkg-008',
  name: 'Bwindi Gorilla Trekking Experience',
  operator: 'Uganda Wild Encounters',
  region: 'Bwindi',
  country: 'Uganda',
  duration: 3,
  durationType: 'days',
  category: 'Primate',
  badge: 'Limited Permits',
  badgeColor: 'bg-red-100 text-red-800',
  price: 340000,
  deposit: 102000,
  currency: 'KES',
  rating: 4.9,
  reviewCount: 156,
  groupMin: 1,
  groupMax: 4,
  spotsLeft: 2,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_151cea54f-1775894309532.png",
  imageAlt: 'Mountain gorilla family in Bwindi Impenetrable Forest Uganda green canopy',
  tags: ['Gorillas', 'Primates', 'Bucket List'],
  includes: ['Gorilla Permit', 'Guide', 'Forest Lodge'],
  saved: false
},
{
  id: 'pkg-009',
  name: 'Ol Pejeta Rhino Conservation',
  operator: 'Conservation Safari Co.',
  region: 'Ol Pejeta',
  country: 'Kenya',
  duration: 2,
  durationType: 'days',
  category: 'Conservation',
  badge: 'Eco Pick',
  badgeColor: 'bg-green-100 text-green-800',
  price: 78000,
  deposit: 23400,
  currency: 'KES',
  rating: 4.8,
  reviewCount: 119,
  groupMin: 2,
  groupMax: 10,
  spotsLeft: 8,
  image: "https://images.unsplash.com/photo-1532574754390-44dc5c6780bb",
  imageAlt: 'White rhino grazing at Ol Pejeta conservancy with acacia trees at dusk',
  tags: ['Rhino', 'Conservation', 'Chimpanzees'],
  includes: ['Game Drives', 'Rhino Tracking', 'Half Board'],
  saved: false
},
{
  id: 'pkg-010',
  name: 'Tarangire Baobab Wilderness',
  operator: 'Tanzania Exclusive Safaris',
  region: 'Tarangire',
  country: 'Tanzania',
  duration: 3,
  durationType: 'days',
  category: 'Photography',
  badge: null,
  badgeColor: '',
  price: 96000,
  deposit: 28800,
  currency: 'KES',
  rating: 4.6,
  reviewCount: 74,
  groupMin: 2,
  groupMax: 6,
  spotsLeft: 10,
  image: "https://images.unsplash.com/photo-1564198729838-cb82ee0c733c",
  imageAlt: 'Ancient baobab trees at sunset in Tarangire National Park Tanzania with elephants',
  tags: ['Photography', 'Baobabs', 'Elephants'],
  includes: ['Photo Workshops', 'Full Board', 'Game Drives'],
  saved: false
},
{
  id: 'pkg-011',
  name: 'Murchison Falls Nile Safari',
  operator: 'Uganda Wild Encounters',
  region: 'Murchison Falls',
  country: 'Uganda',
  duration: 4,
  durationType: 'days',
  category: 'Adventure',
  badge: null,
  badgeColor: '',
  price: 132000,
  deposit: 39600,
  currency: 'KES',
  rating: 4.7,
  reviewCount: 91,
  groupMin: 2,
  groupMax: 10,
  spotsLeft: 7,
  image: "https://images.unsplash.com/photo-1664690126073-15b4ec53b718",
  imageAlt: 'Hippos in the Nile River below Murchison Falls with lush green banks',
  tags: ['Nile Cruise', 'Big Five', 'Waterfalls'],
  includes: ['Nile Boat Safari', 'Full Board', 'Game Drives'],
  saved: false
},
{
  id: 'pkg-012',
  name: 'Selous Game Reserve Fly-In',
  operator: 'Southern Tanzania Expeditions',
  region: 'Selous',
  country: 'Tanzania',
  duration: 5,
  durationType: 'days',
  category: 'Luxury',
  badge: 'Fly-In',
  badgeColor: 'bg-purple-100 text-purple-800',
  price: 420000,
  deposit: 126000,
  currency: 'KES',
  rating: 4.9,
  reviewCount: 48,
  groupMin: 2,
  groupMax: 6,
  spotsLeft: 4,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_177e639c3-1772155358299.png",
  imageAlt: 'Small aircraft landing on bush airstrip in Selous Game Reserve Tanzania',
  tags: ['Fly-In', 'Luxury', 'Boat Safari'],
  includes: ['Charter Flight', 'All Inclusive', 'Boat Safari'],
  saved: false
}];


const CATEGORIES = ['All', 'Big Five', 'Family', 'Luxury', 'Adventure', 'Photography', 'Conservation', 'Birding', 'Primate'];
const REGIONS = ['All Regions', 'Maasai Mara', 'Serengeti', 'Amboseli', 'Tsavo', 'Ngorongoro', 'Samburu', 'Bwindi', 'Lake Nakuru'];
const SORT_OPTIONS = ['Recommended', 'Price: Low to High', 'Price: High to Low', 'Top Rated', 'Newest'];

export default function PackagesGrid() {
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set(['pkg-002', 'pkg-006']));
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeRegion, setActiveRegion] = useState('All Regions');
  const [sortBy, setSortBy] = useState('Recommended');
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [durationFilter, setDurationFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [sharedId, setSharedId] = useState<string | null>(null);
  const [showShareTooltip, setShowShareTooltip] = useState<string | null>(null);

  const { user } = useAuth();
  const supabase = createClient();

  useEffect(() => {
    if (!user) return;
    supabase
      .from('referral_codes')
      .select('code')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.code) setReferralCode(data.code);
      });
  }, [user]);

  const buildShareUrl = (pkgId: string) => {
    const base = `${process.env.NEXT_PUBLIC_SITE_URL}/safari-packages-listing/${pkgId}`;
    return referralCode ? `${base}?ref=${referralCode}` : base;
  };

  const handleShare = async (e: React.MouseEvent, pkgId: string, pkgName: string) => {
    e.preventDefault();
    const url = buildShareUrl(pkgId);
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Check out this safari: ${pkgName}`,
          text: `I found an amazing safari package on Onthego Safari — ${pkgName}. Book through my link and let's both earn rewards!`,
          url,
        });
        setSharedId(pkgId);
        setTimeout(() => setSharedId(null), 2000);
      } catch {
        // user cancelled
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setShowShareTooltip(pkgId);
        setTimeout(() => setShowShareTooltip(null), 2000);
      } catch {
        // fallback silent
      }
    }
  };

  const toggleSave = (id: string) => {
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else
      next.add(id);
      return next;
    });
  };

  const filtered = PACKAGES.filter((p) => {
    if (activeCategory !== 'All' && p.category !== activeCategory) return false;
    if (activeRegion !== 'All Regions' && p.region !== activeRegion) return false;
    if (p.price < priceRange[0] || p.price > priceRange[1]) return false;
    if (durationFilter === '1-2' && p.duration > 2) return false;
    if (durationFilter === '3-4' && (p.duration < 3 || p.duration > 4)) return false;
    if (durationFilter === '5+' && p.duration < 5) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'Price: Low to High') return a.price - b.price;
    if (sortBy === 'Price: High to Low') return b.price - a.price;
    if (sortBy === 'Top Rated') return b.rating - a.rating;
    return 0;
  });

  const itemsPerPage = 9;
  const totalPages = Math.ceil(sorted.length / itemsPerPage);
  const paginated = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <section className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-8">
      {/* Controls Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
        {/* Category Chips */}
        <div className="flex items-center gap-2 flex-wrap flex-1">
          {CATEGORIES.map((cat) =>
          <button
            key={`cat-${cat}`}
            onClick={() => {setActiveCategory(cat);setCurrentPage(1);}}
            className={`px-3.5 py-1.5 text-sm font-medium rounded-full border transition-all duration-150 ${
            activeCategory === cat ?
            'bg-safari-green text-white border-safari-green' : 'bg-white text-gray-600 border-gray-200 hover:border-safari-green hover:text-safari-green'}`
            }>
            
              {cat}
            </button>
          )}
        </div>

        {/* Right Controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border transition-all ${
            showFilters ? 'bg-safari-green text-white border-safari-green' : 'bg-white text-gray-600 border-gray-200 hover:border-safari-green'}`
            }>
            
            <SlidersHorizontal size={15} />
            Filters
          </button>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2">
            <ArrowUpDown size={14} className="text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm font-medium text-gray-700 outline-none bg-transparent">
              
              {SORT_OPTIONS.map((opt) =>
              <option key={`sort-${opt}`} value={opt}>{opt}</option>
              )}
            </select>
          </div>
          <span className="text-sm text-gray-500 whitespace-nowrap">
            <span className="font-semibold text-gray-800">{filtered.length}</span> packages
          </span>
        </div>
      </div>

      {/* Advanced Filter Panel */}
      {showFilters &&
      <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6 fade-in shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Region</label>
              <div className="flex flex-wrap gap-2">
                {REGIONS.map((r) =>
              <button
                key={`region-${r}`}
                onClick={() => setActiveRegion(r)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                activeRegion === r ?
                'bg-safari-yellow text-gray-900 border-safari-yellow' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-safari-yellow'}`
                }>
                
                    {r}
                  </button>
              )}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Duration</label>
              <div className="flex gap-2">
                {[['All', 'All'], ['1-2', '1–2 Days'], ['3-4', '3–4 Days'], ['5+', '5+ Days']].map(([val, label]) =>
              <button
                key={`dur-${val}`}
                onClick={() => setDurationFilter(val)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                durationFilter === val ?
                'bg-safari-yellow text-gray-900 border-safari-yellow' : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-safari-yellow'}`
                }>
                
                    {label}
                  </button>
              )}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">
                Max Price: <span className="text-safari-green font-bold">KES {priceRange[1].toLocaleString()}</span>
              </label>
              <input
              type="range"
              min={0}
              max={500000}
              step={10000}
              value={priceRange[1]}
              onChange={(e) => setPriceRange([0, Number(e.target.value)])}
              className="w-full accent-safari-green" />
            
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>KES 0</span>
                <span>KES 500,000</span>
              </div>
            </div>
          </div>
        </div>
      }

      {/* Package Cards Grid */}
      {paginated.length === 0 ?
      <div className="text-center py-20">
          <div className="w-16 h-16 bg-safari-sand rounded-2xl flex items-center justify-center mx-auto mb-4">
            <MapPin size={28} className="text-safari-earth" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No safari packages found</h3>
          <p className="text-gray-500 text-sm mb-4">Try adjusting your filters or expanding your search criteria.</p>
          <button
          onClick={() => {setActiveCategory('All');setActiveRegion('All Regions');setDurationFilter('All');setPriceRange([0, 500000]);}}
          className="px-5 py-2.5 bg-safari-green text-white text-sm font-semibold rounded-xl hover:bg-green-800 transition-all">
          
            Clear All Filters
          </button>
        </div> :

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
          {paginated.map((pkg) => {
            const isSaved = savedIds.has(pkg.id);
            return (
              <div
                key={pkg.id}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 group flex flex-col"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <AppImage
                    src={pkg.image}
                    alt={pkg.imageAlt}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  {pkg.badge && (
                    <span className={`absolute top-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full ${pkg.badgeColor}`}>
                      {pkg.badge}
                    </span>
                  )}
                  {/* Action buttons: save + share */}
                  <div className="absolute top-3 right-3 flex items-center gap-1.5">
                    {/* Share button */}
                    <div className="relative">
                      <button
                        onClick={(e) => handleShare(e, pkg.id, pkg.name)}
                        className="p-1.5 rounded-lg backdrop-blur-sm bg-white/80 text-gray-500 hover:text-safari-green hover:bg-white transition-all"
                        title="Share & earn referral points"
                      >
                        {sharedId === pkg.id ? (
                          <Check size={14} className="text-safari-green" />
                        ) : (
                          <Share2 size={14} />
                        )}
                      </button>
                      {showShareTooltip === pkg.id && (
                        <div className="absolute right-0 top-8 bg-gray-900 text-white text-[10px] font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap z-10 shadow-lg">
                          Link copied!
                          <div className="absolute -top-1 right-2 w-2 h-2 bg-gray-900 rotate-45" />
                        </div>
                      )}
                    </div>
                    {/* Save button */}
                    <button
                      onClick={(e) => { e.preventDefault(); toggleSave(pkg.id); }}
                      className={`p-1.5 rounded-lg backdrop-blur-sm transition-all ${isSaved ? 'bg-red-500 text-white' : 'bg-white/80 text-gray-500 hover:text-red-500'}`}
                    >
                      <Heart size={14} fill={isSaved ? 'currentColor' : 'none'} />
                    </button>
                  </div>
                  <div className="absolute bottom-3 left-3 flex items-center gap-1">
                    {pkg.spotsLeft <= 3 && (
                      <span className="text-[10px] font-bold bg-red-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Zap size={9} /> {pkg.spotsLeft} left
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-sm font-bold text-gray-900 leading-tight line-clamp-2">{pkg.name}</h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <MapPin size={11} className="flex-shrink-0" />
                    {pkg.region}, {pkg.country}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1"><Clock size={11} />{pkg.duration} days</span>
                    <span className="flex items-center gap-1"><Users size={11} />{pkg.groupMin}–{pkg.groupMax}</span>
                    <span className="flex items-center gap-1"><Star size={11} className="text-safari-yellow fill-safari-yellow" />{pkg.rating}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {pkg.includes.slice(0, 3).map((inc) => (
                      <span key={`inc-${inc}`} className="text-[10px] bg-green-50 text-safari-green font-medium px-2 py-0.5 rounded-md">
                        ✓ {inc}
                      </span>
                    ))}
                  </div>
                  <div className="mt-auto pt-3 border-t border-gray-100 flex items-end justify-between">
                    <div>
                      <p className="text-[10px] text-gray-400">from</p>
                      <p className="text-base font-bold text-gray-900 tabular-nums">KES {pkg.price.toLocaleString()}</p>
                      <p className="text-[10px] text-gray-400">Deposit: KES {pkg.deposit.toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => handleShare(e, pkg.id, pkg.name)}
                        className="flex items-center gap-1 px-2.5 py-2 text-xs font-semibold text-safari-green border border-safari-green/30 bg-green-50 rounded-xl hover:bg-safari-green hover:text-white transition-all active:scale-95"
                        title="Share & earn points"
                      >
                        <Share2 size={11} />
                        Share
                      </button>
                      <Link
                        href={`/safari-packages-listing/${pkg.id}`}
                        className="flex items-center gap-1.5 px-3.5 py-2 bg-safari-green text-white text-xs font-bold rounded-xl hover:bg-green-800 transition-all active:scale-95"
                      >
                        View Details
                        <ChevronRight size={13} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      }

      {/* Pagination */}
      {totalPages > 1 &&
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Showing <span className="font-semibold text-gray-800">{(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, sorted.length)}</span> of <span className="font-semibold text-gray-800">{sorted.length}</span> packages
          </p>
          <div className="flex items-center gap-2">
            <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="p-2 border border-gray-200 rounded-lg hover:border-safari-green hover:text-safari-green disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) =>
          <button
            key={`page-${page}`}
            onClick={() => setCurrentPage(page)}
            className={`w-9 h-9 text-sm font-medium rounded-lg transition-all ${
            currentPage === page ?
            'bg-safari-green text-white' : 'border border-gray-200 text-gray-600 hover:border-safari-green hover:text-safari-green'}`
            }>
            
                {page}
              </button>
          )}
            <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="p-2 border border-gray-200 rounded-lg hover:border-safari-green hover:text-safari-green disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      }
    </section>);

}

interface PackageCardProps {
  pkg: typeof PACKAGES[0];
  isSaved: boolean;
  onToggleSave: () => void;
}

function PackageCard({ pkg, isSaved, onToggleSave }: PackageCardProps) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-200 group flex flex-col">
      {/* Image */}
      <div className="relative h-52 overflow-hidden">
        <AppImage
          src={pkg.image}
          alt={pkg.imageAlt}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-500"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

        {/* Badge */}
        {pkg.badge &&
        <div className={`absolute top-3 left-3 px-2.5 py-1 text-xs font-bold rounded-full ${pkg.badgeColor}`}>
            {pkg.badge}
          </div>
        }

        {/* Save Button */}
        <button
          onClick={onToggleSave}
          className="absolute top-3 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-all active:scale-90">
          
          <Heart
            size={15}
            className={isSaved ? 'fill-red-500 text-red-500' : 'text-gray-500'} />
          
        </button>

        {/* Spots Left */}
        {pkg.spotsLeft <= 3 &&
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-red-500/90 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full">
            <Zap size={11} />
            Only {pkg.spotsLeft} spots left
          </div>
        }

        {/* Duration pill */}
        <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
          <Clock size={11} />
          {pkg.duration} {pkg.durationType}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-bold text-gray-900 leading-tight flex-1">{pkg.name}</h3>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Star size={13} className="fill-safari-yellow text-safari-yellow" />
            <span className="text-sm font-bold text-gray-800 tabular-nums">{pkg.rating}</span>
            <span className="text-xs text-gray-400">({pkg.reviewCount})</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 mb-3">
          <MapPin size={12} className="text-safari-earth flex-shrink-0" />
          <span className="text-xs text-gray-500">{pkg.region}, {pkg.country}</span>
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-400">by {pkg.operator}</span>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {pkg.tags.slice(0, 3).map((tag) =>
          <span key={`${pkg.id}-tag-${tag}`} className="px-2 py-0.5 bg-safari-sand text-earth text-xs font-medium rounded-md">
              {tag}
            </span>
          )}
        </div>

        {/* Includes */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {pkg.includes.map((inc) =>
          <span key={`${pkg.id}-inc-${inc}`} className="text-xs text-gray-500 flex items-center gap-1">
              <span className="w-1 h-1 bg-safari-green rounded-full"></span>
              {inc}
            </span>
          )}
        </div>

        {/* Group size */}
        <div className="flex items-center gap-1.5 mb-4 text-xs text-gray-500">
          <Users size={12} />
          <span>{pkg.groupMin}–{pkg.groupMax} people</span>
        </div>

        {/* Pricing */}
        <div className="mt-auto pt-3 border-t border-gray-100">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">From</p>
              <p className="text-xl font-bold text-gray-900 tabular-nums">
                KES {pkg.price.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 tabular-nums">
                Deposit: <span className="font-semibold text-safari-green">KES {pkg.deposit.toLocaleString()}</span>
              </p>
            </div>
            <Link
              href="/safari-booking"
              className="px-4 py-2.5 bg-safari-yellow text-gray-900 text-sm font-bold rounded-xl hover:bg-yellow-400 transition-all duration-150 active:scale-95">
              
              Book Now
            </Link>
          </div>
        </div>
      </div>
    </div>);

}